import React, { useEffect, useMemo, useState } from "react";
import { MdCancel, MdDownload, MdEdit, MdEditDocument, MdSave } from "react-icons/md";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { fetchAnagraficaById } from "../redux/slices/anagraficaSlice";
import { osservazionePath } from "../utils/functions";
import { downloadZipStorico } from "../redux/slices/agendaSlice";
import { addNotification } from "../redux/slices/notificationSlice";
import { MessageType } from "../types";
import FormNewOsservazionePopup from "./popup/FormNewOsservazionePopup";

interface IProps {
  isEditing?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  isCreating?: boolean;
  handleSave?: () => void;
  handleAnnulla?: () => void;
  searchTerm?: string;
  setSearchTerm?: React.Dispatch<React.SetStateAction<string>>;
}

function AgendaNavbar(props: IProps) {
  const { id } = useParams<{ id: string }>();
  const { selectedAnagrafica } = useAppSelector((state) => state.anagrafica);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("Storico");
  const [showFormNewOsservazione, setShowFormNewOsservazione] = useState(false);

  const tabs = useMemo(() => {
    const baseTabs = [{ name: "Dati anagrafici", path: `/anagrafica/${id}` }];

    if (!selectedAnagrafica?.IsEducatore) {
      return [
        {
          name: "Osservazione",
          path: `/osservazione/${id}/${osservazionePath()}`,
        }, // TODO: NON permettere click diretto
        { name: "Storico", path: `/storico/${id}` },
        ...baseTabs,
      ];
    }

    return baseTabs;
  }, [id, selectedAnagrafica]);

  // Imposto l'activeTab in base alla rotta corrente
  useEffect(() => {
    // Ottieni la prima parte della rotta (ad esempio '/osservazione', '/storico')
    const currentPath = location.pathname.split("/")[1];
    const matchedTab = tabs.find((tab) => tab.path.includes(currentPath));

    if (matchedTab) {
      setActiveTab(matchedTab.name);
    }
  }, [location.pathname, tabs]);

  useEffect(() => {
    if (id && (!selectedAnagrafica || selectedAnagrafica.Id !== Number(id))) {
      // Effettua la fetch solo se non esiste già un'anagrafica selezionata o se l'id è diverso
      dispatch(fetchAnagraficaById(Number(id)));
    }
  }, [id, selectedAnagrafica, dispatch]);

  const handleSave = () => {
    if (props.handleSave) {
      props.handleSave();
    }
  };

  const handleAnnulla = () => {
      if (props.handleAnnulla) {
          props.handleAnnulla();
      }
  };

  const downloadStorico = async () => {
    if (!id) return;
    try {
      const blob = await downloadZipStorico({ id: Number(id) });
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/zip" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `storico_osservazioni.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Errore durante il download del file ZIP:", error);
      dispatch(
        addNotification({
          message: `Errore durante il download del file ZIP: ${error}`,
          type: MessageType.ERROR,
        })
      );
    }
  };

  return (
    <>
      <FormNewOsservazionePopup
          idAnagrafica={Number(id)}
          show={showFormNewOsservazione}
          onClose={() => setShowFormNewOsservazione(false)}
      />
    
      <div className="flex items-end justify-between mb-6">
        <h1 className="h1">
          {props.isCreating
            ? "Nuova anagrafica"
            : selectedAnagrafica
            ? `${selectedAnagrafica.Nome} ${selectedAnagrafica.Cognome}`
            : "Caricamento..."}
        </h1>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-5">
          {tabs.map((tab) => (
            <Link
              to={tab.path}
              key={tab.name}
              onClick={() => setActiveTab(tab.name)}
              className={`px-4 py-2 ${
                id && !selectedAnagrafica?.IsEducatore
                  ? "cursor-pointer"
                  : "pointer-events-none"
              } border border-b-0 border-gray-300 rounded-t-2xl relative ${
                activeTab === tab.name
                  ? "bg-white"
                  : `bg-gray-100 ${id ? "hover:bg-white" : ""}`
              }`}
            >
              {tab.name}
              {activeTab === tab.name && (
                <div className="absolute -bottom-1 left-0 w-full h-2 bg-white z-[800]"></div>
              )}
            </Link>
          ))}
        </div>

        {activeTab === "Osservazione" &&
          (props.isEditing ? (
            <div className='flex items-center gap-4'>
                <button className='btn' disabled={selectedAnagrafica?.IsArchiviato} onClick={handleSave}>Salva <MdSave size={18} /></button>
                <button className='btn' onClick={handleAnnulla}>Annulla <MdCancel size={18} /></button>
            </div>
          ) : (
            <button
              className="btn"
              disabled={selectedAnagrafica?.IsArchiviato}
              onClick={() =>
                props.setIsEditing && props.setIsEditing(!props.isEditing)
              }
            >
              Modifica <MdEdit />
            </button>
          ))}

        {activeTab === "Storico" && (
          <div className="flex items-center gap-4">
            <button
              // to={`/osservazione/${id}/${osservazionePath()}`}
              onClick={() => setShowFormNewOsservazione(true)}
              className={`btn ${
                selectedAnagrafica?.IsArchiviato && " disabled"
              }`}
            >
              Nuova Osservazione <MdEditDocument size={18} />
            </button>
            <button
              className="btn btn-sm btn-outline"
              onClick={downloadStorico}
              title="Scarica tutto"
            >
              <MdDownload size={20} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default AgendaNavbar;
